import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import {
  EmailNotificationSetting,
  GeneralNotificationSettings,
  JobNotificationSetting,
  NotificationSetting,
} from '../../interfaces/settings/notification-setting';
import * as SignatureActions from './notification-settings.actions';

export const notificationSettingsFeatureKey = 'notification-settings';

export interface State extends EntityState<NotificationSetting> {
  // additional entities state properties
  emails: EmailNotificationSetting[];
  notifications: NotificationSetting[];
  jobs: JobNotificationSetting[];
  loadingGeneral: boolean;
  loadingJobs: boolean;
  updatingGeneral: boolean;
  updatingJobs: boolean;
  error: any;
}

export const adapter: EntityAdapter<NotificationSetting> =
  createEntityAdapter<NotificationSetting>();

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  emails: null,
  notifications: null,
  jobs: null,
  loadingGeneral: false,
  loadingJobs: false,
  updatingGeneral: false,
  updatingJobs: false,
  error: null,
});

export const notificationSettingsReducer = createReducer(
  initialState,
  on(SignatureActions.loadNotificationSettingsGeneral, (state) => ({
    ...state,
    loadingGeneral: true,
  })),
  on(
    SignatureActions.loadNotificationSettingsGeneralSuccess,
    (state, action) => ({
      ...state,
      emails: action.emails,
      notifications: action.notifications,
      loadingGeneral: false,
    })
  ),
  on(
    SignatureActions.loadNotificationSettingsGeneralFailure,
    (state, action) => ({
      ...state,
      error: action.error,
      loadingGeneral: false,
    })
  ),
  on(SignatureActions.loadNotificationSettingsJobs, (state) => ({
    ...state,
    loadingJobs: true,
  })),
  on(SignatureActions.loadNotificationSettingsJobsSuccess, (state, action) => ({
    ...state,
    jobs: action.settings,
    loadingJobs: false,
  })),
  on(SignatureActions.loadNotificationSettingsJobsFailure, (state, action) => ({
    ...state,
    error: action.error,
    loadingJobs: false,
  })),
  on(SignatureActions.updateNotificationSettingsGeneral, (state, action) => ({
    action,
    ...state,
    updatingGeneral: true,
  })),
  on(
    SignatureActions.updateNotificationSettingsGeneralSuccess,
    (state, action) => ({
      ...state,
      emails: action.emails,
      notifications: action.notifications,
      updatingGeneral: false,
    })
  ),
  on(
    SignatureActions.updateNotificationSettingsGeneralFailure,
    (state, action) => ({
      ...state,
      error: action.error,
      updatingGeneral: false,
    })
  ),
  on(SignatureActions.updateNotificationSettingsJobs, (state, action) => ({
    action,
    ...state,
    updatingJobs: true,
  })),
  on(
    SignatureActions.updateNotificationSettingsJobsSuccess,
    (state, action) => ({
      ...state,
      jobs: action.settings,
      updatingJobs: false,
    })
  ),
  on(
    SignatureActions.updateNotificationSettingsJobsFailure,
    (state, action) => ({
      ...state,
      error: action.error,
      updatingJobs: false,
    })
  ),
  on(SignatureActions.clearNotificationSettings, (state) =>
    adapter.removeAll(state)
  )
);

export const selectNotificationSettingsState = createFeatureSelector<State>(
  notificationSettingsFeatureKey
);

const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors(selectNotificationSettingsState);

export const selectNotificationSettingsAll = selectAll;
export const selectNotificationSettingsTotal = selectTotal;
export const selectNotificationSettingsEntities = selectEntities;
export const selectNotificationSettingsIds = selectIds;
export const selectNotificationSettingsGeneral = createSelector(
  selectNotificationSettingsState,
  (state): GeneralNotificationSettings => ({
    emails: state.emails,
    notifications: state.notifications,
  })
);
export const selectNotificationSettingsJobs = createSelector(selectNotificationSettingsState, (state) => state.jobs);
export const selectNotificationSettingsLoadingGeneral = createSelector(
  selectNotificationSettingsState,
  (state) => state.loadingGeneral
);
export const selectNotificationSettingsLoadingJobs = createSelector(
  selectNotificationSettingsState,
  (state) => state.loadingJobs
);
